import React from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { SelectItem } from "../../models/SelectItem";
import { Button } from "primereact/button";
import { CreateIbanPaymentMethodRequest, PaymentMethodStatus } from "../../models/MerchantModel";
import { utils } from "../../utils/utils";
import { currenciesApiService } from "../../services/api/merchants/CurrenciesApiService";
import { Check, Loader2 } from "lucide-react";
import { AppConstants } from "../../models/AppConstants";
import { ValidateIbanRequest } from "../../models/MoneyTransferModal";
import { userApiService } from "../../services/api/users/UsersApiService";
import { toastService } from "../../services/toastService";

interface AddBankProps {
    OnSuccess: (item: CreateIbanPaymentMethodRequest) => void;
    selectedMerchantId: number;
    isSubmitting: boolean;
    OnBack?: () => void;
}

interface AddBankState {
    accountName: string;
    bankName: string;
    currency: string;
    iban: string;
    swift: string;
    bankStatement: string;
    bankStatementFileName: string;
    currencies: SelectItem[];
    ibanValidate: boolean;
}

export class AddBank extends React.Component<AddBankProps, AddBankState> {
    bankStatementInput: HTMLInputElement | null = null;

    constructor(props: AddBankProps) {
        super(props);

        this.state = {
            accountName: "",
            bankName: "",
            currency: "BGN",
            iban: "",
            swift: "",
            bankStatement: "",
            bankStatementFileName: "",
            currencies: [],
            ibanValidate: false,
        };
    }

    componentDidMount(): void {
        this.onLoad();
    }

    onLoad = async () => {
        const currenciesResult = await currenciesApiService.getMerchantActiveCurrencies(this.props.selectedMerchantId);
        if (currenciesResult.success) {
            const currencies = currenciesResult.data.map((a) => ({ label: a.charCode, value: a.charCode }) as SelectItem);
            this.setState({ currencies: currencies });
        }
    };

    onValidateIban = async () => {
        if (this.state.iban.length > AppConstants.IbanMinLength && this.state.iban.length <= AppConstants.IbanMaxLength) {
            this.setState({ ibanValidate: true });
            const obj: ValidateIbanRequest = {
                iban: this.state.iban,
            };
            var validIban = await userApiService.validateIban(obj);
            if (!validIban.success) {
                this.setState({ ibanValidate: false, bankName: "", swift: "" });
                toastService.error(validIban.message);
                return;
            }
            this.setState({ bankName: validIban.data.bank, swift: validIban.data.bic, ibanValidate: false });
        }
    };

    setBankStatement = async (file: File) => {
        this.setState({ bankStatementFileName: file.name || "" });
        const data = (await utils.convertFileToBase64(file)) as string;
        this.setState({ bankStatement: data });
    };

    onSubmit = () => {
        const obj = new CreateIbanPaymentMethodRequest();
        obj.accountName = this.state.accountName;
        obj.bankName = this.state.bankName;
        obj.bankStatement = this.state.bankStatement;
        obj.bankStatementFileName = this.state.bankStatementFileName;
        obj.ibanNumber = this.state.iban;
        obj.swift = this.state.swift;
        obj.currency = this.state.currency;
        obj.status = PaymentMethodStatus.Approve;
        this.props.OnSuccess(obj);
    };

    render(): React.ReactNode {
        return (
            <div className="space-y-5 p-1">
                {/* IBAN validation overlay */}
                {this.state.ibanValidate && (
                    <div className="flex items-center gap-2 text-sm text-neutral-500 dark:text-neutral-400">
                        <Loader2 size={16} className="animate-spin" />
                        <span>Validating IBAN...</span>
                    </div>
                )}

                {/* Account Name */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Account Name</label>
                    <InputText
                        value={this.state.accountName}
                        type="text"
                        placeholder="Account Name"
                        onChange={(e) => this.setState({ accountName: e.target.value })}
                        className="w-full"
                    />
                </div>

                {/* IBAN + Bank Name */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">IBAN</label>
                        <InputText
                            value={this.state.iban}
                            type="text"
                            placeholder="Enter IBAN"
                            onChange={(e) => this.setState({ iban: e.target.value })}
                            onBlur={this.onValidateIban}
                            className="w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Bank Name</label>
                        <InputText
                            value={this.state.bankName}
                            type="text"
                            readOnly
                            className="w-full"
                            placeholder="Auto-filled from IBAN"
                        />
                    </div>
                </div>

                {/* Swift + Currency */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Swift</label>
                        <InputText
                            value={this.state.swift}
                            type="text"
                            readOnly
                            className="w-full"
                            placeholder="Auto-filled from IBAN"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Currency</label>
                        <Dropdown
                            className="w-full"
                            value={this.state.currency}
                            options={this.state.currencies}
                            onChange={(e) => this.setState({ currency: e.value })}
                            placeholder="Select Currency"
                        />
                    </div>
                </div>

                {/* Bank Statement */}
                <div className="space-y-1.5">
                    <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400">Bank Statement</label>
                    <InputText
                        ref={(el) => (this.bankStatementInput = el)}
                        type="file"
                        onChange={(e) => e.target.files && this.setBankStatement(e.target.files[0])}
                        className="w-full"
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-2">
                    {this.props.OnBack && (
                        <Button
                            label="Back"
                            outlined
                            onClick={this.props.OnBack}
                            disabled={this.props.isSubmitting}
                        />
                    )}
                    <Button
                        label="Submit"
                        icon={<Check size={16} />}
                        loading={this.props.isSubmitting}
                        disabled={this.props.isSubmitting}
                        onClick={this.onSubmit}
                    />
                </div>
            </div>
        );
    }
}
